import { AppThunkAction } from "..";
import { KnownAction } from "./actionTypes";
import { OJEUHub } from "./signalRHub";
import {
    OJEUSettings,
    Mode,
    Errors,
    DisplaySettings,
    RegulationSettings,
    HeatingSettings,
    FloorSensorSettings,
    AwayModeSettings,
    DateTimeSettings,
    WifiSettings,
    WarrantyInfo,
    DeviceInfo,
    CreateOJEURequest,
    LocaleSettings,
    HydronicControl,
    ClockSettings,
    PSUFunctionMode,
    ProtectiveFunctions
} from "./models";
import { toBase64 } from "../../Utility";

export enum OJEUAction {
    CreateNew = "CreateNew",
    Remove = "RemoveThermostat",
    Connect = "Connect",
    Link = "Link",
    Unlink = "Unlink",
    Disconnect = "Disconnect",
    IncreaseSetpoint = "IncreaseSetpoint",
    DecreaseSetpoint = "DecreaseSetpoint",
    Save = "SaveThermostat",
    ToggleTimer = "ToggleTimer",
    ToggleHeatRelay = "ToggleHeatRelay",
    ToggleOpenWindowDetected = "ToggleOpenWindowDetected",
    ToggleFrostProtection = "ToggleFrostProtection",
    ToggleReconnect = "ToggleReconnect",
    FactoryReset = "RequestFactoryReset"
}

export const actionCreator = {
    createNew: (workspace: number, request: CreateOJEURequest | null = null, amount: number = 1): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("CreateNewThermostat", workspace, amount, request).catch(alert);
    },
    addExisting: (workspace: number, email: string, pageNumber: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("AddExisting", workspace, email, pageNumber).catch(alert);
    },
    invoke: (id: string, action: OJEUAction): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send(action, id).catch(alert);
    },
    updateSettings: (id: string, settings: OJEUSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateSettings", id, settings).catch(alert);
    },
    updateProtectiveFunctions: (id: string, protectiveFunctions: ProtectiveFunctions): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateProtectiveFunctions", id, protectiveFunctions).catch(alert);
    },
    setSetpoint: (id: string, setpoint: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetSetpoint", id, setpoint).catch(alert);
    },
    setAutoMode: (id: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetAutoMode", id).catch(alert);
    },
    setManualMode: (id: string, setpoint: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetManualMode", id, setpoint).catch(alert);
    },
    setHoldMode: (id: string, setpoint: number, endTime: Date | null, isPermanent: boolean | null): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetHoldMode", id, setpoint, endTime, isPermanent).catch(alert);
    },
    cancelHoldMode: (id: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("CancelHoldMode", id).catch(alert);
    },
    setServerUrl: (serverUrl: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetServerUrl", serverUrl).catch(alert);
    },
    setErrors: (id: string, errors: Errors): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetErrors", id, errors).catch(alert);
    },
    setStandbyState: (id: string, isInStandby: boolean): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetStandbyState", id, isInStandby).catch(alert);
    },
    setCurrentScheduleEvent: (id: string, day: number, eventId: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetCurrentScheduleEvent", id, day, eventId).catch(alert);
    },
    setCurrentTemp: (id: string, newTemp: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetCurrentTemp", id, newTemp).catch(alert);
    },
    setHumidity: (id: string, newHumidity: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetHumidity", id, newHumidity).catch(alert);
    },
    setPSUFunctionMode: (id: string, psuFunctionMode: PSUFunctionMode): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SetPSUFunctionMode", id, psuFunctionMode).catch(alert);
    },
    fetchLoadableThermostats: (): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("FetchLoadableThermostats").catch(alert);
    },
    loadThermostats: (ids: string[]): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("LoadThermostats", ids).catch(alert);
    },
    deleteThermostats: (ids: string[]): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("DeleteThermostats", ids).catch(alert);
    },
    updateDeviceInfo: (id: string, deviceInfo: DeviceInfo): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        const obj = {
            ...deviceInfo,
            mcuUuid: toBase64(deviceInfo.mcuUuid),
            protocolVersion: toBase64(deviceInfo.protocolVersion)
        };
        OJEUHub.send("UpdateDeviceInfo", id, obj).catch(alert);
    },
    updateDisplaySettings: (id: string, settings: DisplaySettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateDisplaySettings", id, settings).catch(alert);
    },
    updateWifiSettings: (id: string, settings: WifiSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateWifiSettings", id, settings).catch(alert);
    },
    updateRegulationSettings: (id: string, settings: RegulationSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateRegulationSettings", id, settings).catch(alert);
    },
    updateHeatingSettings: (id: string, settings: HeatingSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateHeatingSettings", id, settings).catch(alert);
    },
    updateFloorSensorSettings: (id: string, settings: FloorSensorSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateFloorSensorSettings", id, settings).catch(alert);
    },
    updateAwayModeSettings: (id: string, settings: AwayModeSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateAwayModeSettings", id, settings).catch(alert);
    },
    updateLocaleSettings: (id: string, settings: LocaleSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateLocaleSettings", id, settings).catch(alert);
    },
    updateHydronicControl: (id: string, settings: HydronicControl): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateHydronicControl", id, settings).catch(alert);
    },
    updateClockSettings: (id: string, settings: ClockSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateClockSettings", id, settings).catch(alert);
    },
    updateDateTimeSettings: (id: string, settings: DateTimeSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateDateTimeSettings", id, settings).catch(alert);
    },
    updateWarrantyInfo: (id: string, info: WarrantyInfo): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("UpdateWarrantyInfo", id, info).catch(alert);
    },
    searchDistributors: (query: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SearchDistributors", query).catch(alert);
    },
    searchUsers: (query: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUHub.send("SearchUsers", query).catch(alert);
    }
};